import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useDebounce from '@/components/hooks/useDebounce';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import { useAccessToken } from '@/services/auth';
import {
  type OnFarmImplantTrip,
  onFarmImplantTripsOptions,
} from '@/services/on-farm-implant';

type TripComboboxProps = {
  value?: string;
  onChange?: (tripId: string | undefined) => void;
  placeholder?: string;
  className?: string;
};

export const TripCombobox = ({
  value,
  onChange,
  placeholder,
  className,
}: TripComboboxProps) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const [tripSearch, setTripSearch] = useState('');
  const debouncedSearch = useDebounce(tripSearch);

  const { data: trips } = useQuery(
    onFarmImplantTripsOptions(token, { search: debouncedSearch }),
  );

  const selectedTrip = value
    ? (trips?.find((trip) => trip.tripId === value) ?? null)
    : null;

  // A selected trip drives the input; free text only matters while searching.
  const inputValue = selectedTrip ? selectedTrip.tripNumber : tripSearch;

  const handleChange = (trip: OnFarmImplantTrip | null) => {
    onChange?.(trip?.tripId);
  };

  return (
    <Combobox
      items={trips}
      value={selectedTrip}
      onValueChange={handleChange}
      itemToStringValue={(trip) => trip.tripId}
      itemToStringLabel={(trip) => trip.tripNumber}
    >
      <ComboboxInput
        placeholder={placeholder ?? t('onFarmImplant.sessions.tripNumber')}
        className={cn('w-full', className)}
        value={inputValue}
        onChange={(e) => {
          setTripSearch(e.target.value);
          handleChange(null);
        }}
        showClear
      />
      <ComboboxContent>
        <ComboboxEmpty>
          {t('onFarmImplant.sessions.noTripsFound')}
        </ComboboxEmpty>
        <ComboboxList>
          {(trip: OnFarmImplantTrip) => (
            <ComboboxItem key={trip.tripId} value={trip}>
              {trip.tripNumber}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
